import { Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { useUnreadRecalls, UnreadRecall } from '@/hooks/useUnreadRecalls';
import { Bell } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface NotificationBellProps {
  externalIsOpen?: boolean;
  externalSetIsOpen?: (open: boolean) => void;
}

// Mapping pathogène → emoji + label
const getRiskDisplay = (riskType: string): { emoji: string; label: string } => {
  const map: Record<string, { emoji: string; label: string }> = {
    'listeria': { emoji: '🦠', label: 'Listeria' },
    'salmonella': { emoji: '🦠', label: 'Salmonelle' },
    'ecoli': { emoji: '🦠', label: 'E. coli' },
    'toxoplasma': { emoji: '🔬', label: 'Toxoplasme' },
    'staphylococcus': { emoji: '🦠', label: 'Staphylocoque' },
    'campylobacter': { emoji: '🦠', label: 'Campylobacter' },
    'bacillus_cereus': { emoji: '🦠', label: 'Bacillus cereus' },
    'clostridium_botulinum': { emoji: '🦠', label: 'Botulisme' },
    'clostridium_perfringens': { emoji: '🦠', label: 'Clostridium' },
    'norovirus': { emoji: '🦠', label: 'Norovirus' },
    'rotavirus': { emoji: '🦠', label: 'Rotavirus' },
    'pathogene_autre': { emoji: '🦠', label: 'Pathogène' },
    'allergene': { emoji: '⚠️', label: 'Allergène' },
    'chimique_moah_pah': { emoji: '🧪', label: 'Contaminant' },
    'pesticide_eto': { emoji: '🧪', label: 'Pesticide' },
    'mycotoxine_aflatoxine': { emoji: '🧪', label: 'Mycotoxine' },
    'mycotoxine_ochratoxine': { emoji: '🧪', label: 'Mycotoxine' },
    'conditionnement': { emoji: '📦', label: 'Emballage' },
    'risque_autre': { emoji: '⚠️', label: 'Alerte' },
  };
  return map[riskType] || { emoji: '⚠️', label: 'Alerte' };
};

// Mapping catégorie → badge compact
const getCategoryBadge = (categoryId: string): { emoji: string; label: string } | null => {
  const map: Record<string, { emoji: string; label: string }> = {
    '25': { emoji: '👶', label: 'Bébé' },
    '64': { emoji: '🥛', label: 'Laitiers' },
    '40': { emoji: '🍽️', label: 'Plat préparé' },
    '20': { emoji: '🥓', label: 'Charcuterie' },
    '18': { emoji: '🥩', label: 'Viande' },
    '19': { emoji: '🐟', label: 'Poisson' },
    '21': { emoji: '🥚', label: 'Œufs' },
  };
  return map[categoryId] || null;
};

// Bordure colorée selon sévérité
const getSeverityBorderClass = (score: number | null): string => {
  if (!score || score < 3) return 'border-l-muted-foreground/50';
  if (score >= 4) return 'border-l-destructive';
  return 'border-l-orange-500';
};

// Badge sévérité avec couleur
const getSeverityBadge = (score: number | null) => {
  if (!score) return <Badge variant="secondary" className="text-xs px-1.5 py-0">Modéré</Badge>;
  if (score >= 4) {
    return <Badge variant="destructive" className="text-xs px-1.5 py-0">🔴 Critique</Badge>;
  } else if (score >= 3) {
    return <Badge className="text-xs px-1.5 py-0 bg-orange-500 hover:bg-orange-600">🟠 Élevé</Badge>;
  } else {
    return <Badge variant="secondary" className="text-xs px-1.5 py-0">Modéré</Badge>;
  }
};

// Badge grossesse si pathogène critique
const PREGNANCY_PATHOGENS = ['listeria', 'toxoplasma', 'salmonella'];
const showPregnancyBadge = (riskType: string, categoryId: string): boolean => {
  return PREGNANCY_PATHOGENS.includes(riskType) && categoryId !== '25';
};

export const NotificationBell = ({ 
  externalIsOpen, 
  externalSetIsOpen 
}: NotificationBellProps) => {
  const navigate = useNavigate();
  const {
    recalls,
    unreadCount,
    hasNewRecall,
    isOpen: internalIsOpen,
    setIsOpen: internalSetIsOpen,
    loading,
    markAllAsSeen,
    handleRecallClick
  } = useUnreadRecalls();

  const isOpen = externalIsOpen !== undefined ? externalIsOpen : internalIsOpen;
  const setIsOpen = externalSetIsOpen || internalSetIsOpen;

  // Format recall for display
  const formatRecall = (recall: UnreadRecall) => {
    return {
      title: recall.product_name,
      body: recall.brand,
      time: new Date(recall.published_at).toLocaleString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      }),
    };
  };

  const handleMarkAllAsSeen = async () => {
    await markAllAsSeen();
  };

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="sm" className="relative" aria-label="Notifications">
          <Bell className={cn("h-5 w-5 origin-top", hasNewRecall && "animate-bell-ring")} />
          {unreadCount > 0 && (
            <Badge 
              variant="destructive" 
              className="absolute -top-1 -right-1 h-5 w-5 rounded-full p-0 flex items-center justify-center text-xs"
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </Badge>
          )}
        </Button>
      </SheetTrigger>
      
      <SheetContent className="w-96">
        <SheetHeader className="flex flex-row items-center justify-between pb-4">
          <SheetTitle>Nouveaux rappels</SheetTitle>
          <div className="flex items-center gap-2">
            {recalls.length > 0 && (
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-8 px-2"
                onClick={handleMarkAllAsSeen}
              >
                <Check className="h-4 w-4 mr-1" />
                <span className="text-xs">Marquer vus</span>
              </Button>
            )}
          </div>
        </SheetHeader>
        
        <ScrollArea className="h-[calc(100vh-120px)] mt-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-sm text-muted-foreground">Chargement...</div>
            </div>
          ) : recalls.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-center space-y-2">
              <div className="text-sm text-muted-foreground">Aucun nouveau rappel</div>
              <p className="text-xs text-muted-foreground">
                Tous les rappels publiés depuis votre dernière visite apparaîtront ici.
              </p>
              <Button 
                variant="link" 
                size="sm" 
                onClick={() => {
                  setIsOpen(false);
                  navigate('/settings');
                }}
              >
                Gérer mes préférences push
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {recalls.map((recall) => {
                const formatted = formatRecall(recall);
                const riskDisplay = getRiskDisplay(recall.risk_type);
                const categoryBadge = getCategoryBadge(recall.category_id);
                const borderClass = getSeverityBorderClass(recall.severity_score);
                const showPregnancy = showPregnancyBadge(recall.risk_type, recall.category_id);
                
                return (
                  <div
                    key={recall.id}
                    className={cn(
                      "relative p-3 rounded-lg border-l-4 border transition-colors cursor-pointer hover:bg-muted/50",
                      borderClass,
                      "bg-card"
                    )}
                    onClick={() => handleRecallClick(recall)}
                  >
                    {/* Ligne 1: Emoji + Pathogène */}
                    <div className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground">
                      <span>{riskDisplay.emoji}</span>
                      <span>{riskDisplay.label}</span>
                    </div>
                    
                    {/* Ligne 2-3: Produit + Marque */}
                    <h4 className="text-sm font-semibold mt-1 line-clamp-1">
                      {formatted.title}
                    </h4>
                    <p className="text-xs text-muted-foreground line-clamp-1">
                      {formatted.body}
                    </p>
                    
                    {/* Ligne 4: Badges */}
                    <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                      {getSeverityBadge(recall.severity_score)}
                      {categoryBadge && (
                        <Badge variant="outline" className="text-xs px-1.5 py-0">
                          {categoryBadge.emoji} {categoryBadge.label}
                        </Badge>
                      )}
                      {showPregnancy && (
                        <Badge variant="outline" className="text-xs px-1.5 py-0 border-pink-300 text-pink-600">
                          🤰 Grossesse
                        </Badge>
                      )}
                    </div>
                    
                    {/* Ligne 5: Date */}
                    <p className="text-xs text-muted-foreground mt-1.5">
                      📅 {formatted.time}
                    </p>
                    
                    {/* Indicateur non lu */}
                    <div className="absolute top-3 right-3 w-2 h-2 rounded-full bg-primary" />
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
};
